/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/components/Modals/Firmalar/FirmaDetayModal.js
import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton,
  ModalBody, VStack, HStack, Text,
  Input, useToast, Box, Icon, Flex, Grid, Button,
  Checkbox, Divider,
} from "@chakra-ui/react";
import { FiEdit3, FiBriefcase, FiMail, FiPhone, FiMapPin, FiFileText, FiLock } from "react-icons/fi";
import api from "utils/api";

function InfoItem({ icon, label, value, field, onEdit, isPassword = false }) {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!onEdit || !tempValue.trim()) return;
    
    setLoading(true);
    try {
      await onEdit(field, tempValue);
      setEditing(false);
      setTempValue("");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setTempValue("");
  };

  // Telefon numarası alanları için placeholder belirle
  const getPlaceholder = () => {
    if (isPassword) return "Yeni şifre";
    if (field === "irtibat" || field === "sabit_no") return "05350894034";
    return label;
  };

  return (
    <Box
      p={3}
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      transition="all 0.2s ease"
      _hover={{ borderColor: "blue.300" }}
    >
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={2}>
          <Icon as={icon} color="blue.500" boxSize={3} />
          <Text fontSize="xs" fontWeight="medium" color="gray.600">
            {label}
          </Text>
        </HStack>
        {!editing && (
          <Button
            size="xs"
            variant="ghost"
            colorScheme="blue"
            onClick={() => setEditing(true)}
            opacity={0.7}
            _hover={{ opacity: 1 }}
          >
            <FiEdit3 size={12} />
          </Button>
        )}
      </Flex>
      
      {editing ? (
        <VStack spacing={2} align="stretch">
          <Input
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            placeholder={getPlaceholder()}
            type={isPassword ? "password" : "text"}
            size="sm"
            borderRadius="md"
          />
          <HStack spacing={2}>
            <Button
              size="xs"
              colorScheme="blue"
              onClick={handleSave}
              isLoading={loading}
              flex={1}
            >
              Kaydet
            </Button>
            <Button
              size="xs"
              variant="ghost"
              onClick={handleCancel}
              flex={1}
            >
              İptal
            </Button>
          </HStack>
        </VStack>
      ) : (
        <Text fontSize="sm" fontWeight="500" color="gray.800">
          {isPassword ? "••••••••" : value || "—"}
        </Text>
      )}
    </Box>
  );
}

function FirmaDetayModal({ isOpen, onClose, firma, refreshList }) {
  const toast = useToast();
  const [localFirma, setLocalFirma] = useState(firma);
  const [trProfile, setTrProfile] = useState(null);
  const [trProfileLoading, setTrProfileLoading] = useState(false);
  const [userLimitSaving, setUserLimitSaving] = useState(false);

  useEffect(() => {
    setLocalFirma(firma);
  }, [firma]);

  useEffect(() => {
    if (!isOpen || !localFirma?.HspId) {
      setTrProfile(null);
      return;
    }
    let cancel = false;
    (async () => {
      setTrProfileLoading(true);
      try {
        const r = await api.get(`/api/firmalar/${localFirma.HspId}/traccar-profile`);
        const j = await r.json();
        if (!cancel && r.ok) setTrProfile(j);
        else if (!cancel) setTrProfile(null);
      } catch {
        if (!cancel) setTrProfile(null);
      } finally {
        if (!cancel) setTrProfileLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [isOpen, localFirma?.HspId]);

  if (!localFirma) return null;

  const handleUpdate = async (field, newValue) => {
    try {
      let endpoint = `/api/firmalar/${localFirma.HspId}`;
      let payload = {};

      if (field === "password") {
        endpoint = `/api/firmalar/${localFirma.HspId}/password`;
        payload = { password: newValue };
      } else {
        payload = { [field]: newValue };
      }

      const response = await api.put(endpoint, payload);

      if (!response.ok) throw new Error("Güncelleme hatası");

      const updated = await response.json();

      if (field !== "password") {
        setLocalFirma((prev) => ({ ...prev, ...updated }));
      }

      toast({
        title: "Başarıyla güncellendi",
        description: field === "password" ? "Şifre başarıyla değiştirildi" : `${field} alanı güncellendi`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      if (refreshList) refreshList();
    } catch (err) {
      toast({
        title: "Güncelleme hatası",
        description: field === "password" ? "Şifre güncellenemedi" : "Firma bilgileri güncellenemedi",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
      throw err;
    }
  };

  const handleUserLimitChange = async (checked) => {
    if (!localFirma?.HspId) return;
    setUserLimitSaving(true);
    try {
      const res = await api.put(`/api/firmalar/${localFirma.HspId}`, {
        userLimit: checked ? 50 : -1,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Yetki güncellenemedi");
      }
      setTrProfile((prev) =>
        prev ? { ...prev, userLimit: checked ? 50 : -1 } : prev
      );
      toast({
        title: "Güncellendi",
        description: checked
          ? "Alt kullanıcı oluşturma açıldı."
          : "Alt kullanıcı oluşturma kapatıldı.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      refreshList?.();
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message || "Traccar yetkisi güncellenemedi",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setUserLimitSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay 
        bg="blackAlpha.400" 
        backdropFilter="blur(4px)"
      />
      <ModalContent 
        borderRadius="2xl" 
        boxShadow="xl" 
        m={4}
        maxH="85vh"
        overflow="hidden"
        bg="white"
        p={1}
      >
        {/* Header */}
        <Box
          bg="white"
          p={4}
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              {localFirma.firma}
            </Text>
            <Text fontSize="xs" color="gray.600">
              ID: {localFirma.HspId}
            </Text>
          </VStack>
        </Box>

        <ModalBody px={6} py={4} maxH="500px" overflowY="auto">
          <VStack align="stretch" spacing={5}>
            {/* İletişim Bilgileri */}
            <Box>
              <Text fontSize="md" fontWeight="bold" color="gray.700" mb={3}>
                İletişim Bilgileri
              </Text>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <InfoItem 
                  icon={FiBriefcase}
                  label="Firma Adı" 
                  value={localFirma.firma} 
                  field="firma_ismi" 
                  onEdit={handleUpdate} 
                />
                <InfoItem 
                  icon={FiMail}
                  label="E-posta" 
                  value={localFirma.email} 
                  field="email" 
                  onEdit={handleUpdate} 
                />
                <InfoItem 
                  icon={FiPhone}
                  label="Telefon" 
                  value={localFirma.irtibat} 
                  field="irtibat" 
                  onEdit={handleUpdate} 
                />
                <InfoItem 
                  icon={FiPhone}
                  label="Sabit Telefon" 
                  value={localFirma.sabit_no} 
                  field="sabit_no" 
                  onEdit={handleUpdate} 
                />
              </Grid>
            </Box>

            {/* Firma Detayları */}
            <Box>
              <Text fontSize="md" fontWeight="bold" color="gray.700" mb={3}>
                Firma Detayları
              </Text>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <InfoItem 
                  icon={FiFileText}
                  label="Vergi Numarası" 
                  value={localFirma.vergi_no} 
                  field="vergi_no" 
                  onEdit={handleUpdate} 
                />
                <InfoItem 
                  icon={FiMapPin}
                  label="Adres" 
                  value={localFirma.adres} 
                  field="adres" 
                  onEdit={handleUpdate} 
                />
                <InfoItem 
                  icon={FiMapPin}
                  label="Açık Adres" 
                  value={localFirma.acik_adres} 
                  field="acik_adres" 
                  onEdit={handleUpdate} 
                />
              </Grid>
              <Grid templateColumns="1fr" gap={4} mt={4}>
                <InfoItem 
                  icon={FiLock}
                  label="Şifre Değiştir" 
                  value="" 
                  field="password" 
                  onEdit={handleUpdate} 
                  isPassword 
                />
              </Grid>
            </Box>

            <Box>
              <Text fontSize="md" fontWeight="bold" color="gray.700" mb={3}>
                Yetkiler
              </Text>
              <Divider mb={3} />
              <Checkbox
                isChecked={trProfile ? trProfile.userLimit !== -1 : false}
                isDisabled={trProfileLoading || !trProfile || userLimitSaving}
                onChange={(e) => handleUserLimitChange(e.target.checked)}
                colorScheme="blue"
                size="sm"
              >
                <Text as="span" fontSize="sm">
                  Alt kullanıcı oluşturabilir
                </Text>
              </Checkbox>
              <Text fontSize="xs" color="gray.500" mt={2} ml={6}>
                Traccar kullanıcı limiti: kapalıyken -1, açıkken 50 (sistem normalize eder).
              </Text>
            </Box>
          </VStack>
        </ModalBody>

      </ModalContent>
    </Modal>
  );
}

export default FirmaDetayModal;